import { FontFamily } from './fontFamily/fontFamily';
import localFontManager from './localFontManager';
import webFontManager from './webFontManager';
import { ArrayHelper } from '@/helper/arrayHelper';
import { UserAgentHelper } from '@/helper/userAgentHelper';
import { Const } from '@/const';
import { Document } from '../document2/document';
import { StringHelper } from '@/helper/stringHelper';
import ipcApi from '@/api/ipc-api'
import { DebugHelper } from '@/helper/debugHelper';

// フォント管理クラス

export class FontManager {
    // フォントファミリー
    private fontFamilyArray: Array<FontFamily> = new Array();
    // フォントファミリー連想配列(検索用)
    private static fontFamilyDictionary: { [fontId: string]: FontFamily } = {};
    // デフォルトフォント
    public static readonly DEFAULT_FONT_ID = 'noto-sans-jp-400';
    // 明朝デフォルト
    public static readonly DEFAULT_MINCHO_FONT_ID = 'noto-serif-jp-400';

    private static s_webfont_id_array : Array<string> = [];
    public static isWebFont(fontId : string)
    {
        return this.s_webfont_id_array.indexOf(fontId) >= 0;
    }
    public static UseAlternativeOutlineToBold(fontId : string)
    {
        // HG/P/S教科書体は「太字」設定のとき「夫」が正しく太字にならないので太字にせず、縁取りで対処する
        return fontId == "HGSKyokashotai" || fontId == "HGPKyokashotai" || fontId == "HGKyokashotai";
    }
    public static convertLocalToWebFontIfNeeded(fontId : string)
    {
        // DL版の場合はインストールフォントを確認する
        if(UserAgentHelper.isElectron())
        {
            if(this.fontFamilyDictionary[fontId] != null)
            {
                // フォントが存在していればコンバートしない
                return fontId;
            }
        }

        var convertedFontId = this.DEFAULT_FONT_ID;
        for(var i = 0; i < Const.FONT_CONVERT_MAP.length; ++i)
        {
            if(Const.FONT_CONVERT_MAP[i].name == fontId)
            {
                convertedFontId = Const.FONT_CONVERT_MAP[i].id;
                break;
            }
        }
        return convertedFontId;
    }

    // 読み込みできなかったフォントの一時保存用
    // DL版ファイルの読み込み中にローカルフォントが見つかった場合はここに追加していく
    // 読み込み完了後にdocument - unloadedFonts に入れて、clearしておく
    private static s_store_unloaded_font_ids: Array<string> = [];
    public static clearUnloadedFontIds()
    {
        this.s_store_unloaded_font_ids = [];
    }
    public static storeUnloadedFontIdIfNew(fontId : string)
    {
        // 含まれていれば追加しない
        if(this.s_store_unloaded_font_ids.indexOf(fontId) >= 0) return;
        this.s_store_unloaded_font_ids.push(fontId);
    }
    public static applyUnloadedFontsToDocument(doc : Document)
    {
        doc.unloadedFonts = doc.unloadedFonts.concat(this.s_store_unloaded_font_ids);
        this.clearUnloadedFontIds();
    }

    public ready: boolean = false;

    constructor() {
        // ipcApiが完了しないとloadFontFamilyが正常に動作しない
        // ただしdata.tsのほうでipcApiを呼び出しているので、ここでは完了が判定されるまでをフラグで判定する
        setTimeout(() => {
            this.loadFontFamilyIfPossible();
        }, 1000 / 33);
        // ipcApi().then(() => {
        //     this.loadFontFamily();
        // })
    }

    private loadFontFamilyIfPossible() {
        if(localStorage.getItem("ipc-api-state") === "loading") {
            DebugHelper.trace("fontManager >> ipc loading...");
            setTimeout(() => {
                this.loadFontFamilyIfPossible();
            }, 1000 / 33);
        } else {
            DebugHelper.trace("fontManager >> ipc loaded -> loadFontFamily");
            this.loadFontFamily();
        }
    }

    public loadFontFamily(): Promise<void> {
        this.ready = false;
        return this.initFontFamily().then(() => {
            this.ready = true;
        });
    }

    // フォントの数
    public getFontNumber() {
        return this.fontFamilyArray.length;
    }
    // フォントファミリーの取得
    public getFontFamily(index: number) {
        return this.fontFamilyArray[index];
    }
    public getFontFamilyById(id: string) {
        // 連想配列で高速に返す
        return FontManager.fontFamilyDictionary[id];
        // var index = -1;
        // for(var i = 0; i < this.getFontNumber(); ++i)
        // {
        //     if(this.getFontFamily(i).id == id)
        //     {
        //         index = i;
        //         break;
        //     }
        // }

        // if(index >= 0)
        // {
        //     return this.getFontFamily(index);
        // }
        // // フォントが見つからなかった場合は、ゴシックを返す
        // return new FontFamily(
        //     "noto-serif-jp-400",
        //     "Noto Serif JP Regular",
        //     "Noto Serif JP",
        //     "",
        //     "",
        //     "",
        //     "serif",
        //     "Noto Serif JP",
        //     "Noto_Serif_JP_V_Regular",
        //     400);
    }



    // フォントファミリー名取得
    public getFontFamilyName(id: string, isHankaku = true, isRender = false, isVertical = false, isRenderingOnClient = false) {
        var fontFamily = this.getFontFamilyById(id);
        if (!isHankaku && fontFamily.isUseJPFont) {
            // 日本語代替フォントを使う
            fontFamily = this.getFontFamilyById(fontFamily.idJP);
        }

        if (isRender) {
            // 縦書きかつ半角以外なら縦書きフォント
            var renderFont = isVertical && !isHankaku ? fontFamily.renderingFontNameVertical : fontFamily.renderingFontName;
            return "'" + renderFont + "'";
        } else {
            return fontFamily.toString();
        }
    }

    public getSimpleTextFontFamilyName(id : string, isRender = false, isRenderingOnClient = false)
    {
        var fontFamily = this.getFontFamilyById(id);
        // 通常フォント名取得
        var fontName = isRender ? "'" + fontFamily.renderingFontName + "'" : fontFamily.toStringSimpleText();

        if(fontFamily.isUseJPFont)
        {
            // 日本語代替フォントの取得
            var idJP = fontFamily.idJP;
            // ただし、明朝体はすべてゴシックに変更する
            if(idJP == "noto-serif-jp-200") idJP = "noto-sans-jp-100";
            else if(idJP == "noto-serif-jp-300") idJP = "noto-sans-jp-300";
            else if(idJP == "noto-serif-jp-400") idJP = "noto-sans-jp-400";
            else if(idJP == "noto-serif-jp-500") idJP = "noto-sans-jp-500";
            else if(idJP == "noto-serif-jp-600") idJP = "noto-sans-jp-700"; // 600は存在しない
            else if(idJP == "noto-serif-jp-700") idJP = "noto-sans-jp-700";
            else if(idJP == "noto-serif-jp-900") idJP = "noto-sans-jp-900";
            // レンダリングフォント取得
            var altFont = this.getFontFamilyById(idJP);
            var altName = isRender ? "'" + altFont.renderingFontName + "'" : altFont.toStringSimpleText();
            fontName += "," + altName;
        }

        if(!isRender) fontName += ",sans-serif";

        return fontName;
    }

    // イタリック可能か
    public canItalic(id: string, isHankaku = false) {
        var fontFamily = this.getFontFamilyById(id);
        if (!isHankaku && fontFamily.isUseJPFont) {
            // 日本語代替フォントを使う
            fontFamily = this.getFontFamilyById(fontFamily.idJP);
        }

        return fontFamily.canItalic;
    }

    // フォントファミリーを初期化する
    private initFontFamily(): Promise<void> {
        return new Promise(resolve => {
            webFontManager.getAvailableFonts().then(webFonts => {
                if (UserAgentHelper.isElectron()) {
                    localFontManager.getAvailableFonts().then(localFonts => {
                        this.setFontFamily(localFonts.concat(webFonts));
                        resolve();
                    });
                } else {
                    this.setFontFamily(webFonts);
                    resolve();
                }
            });
        });
    }

    private setFontFamily(fonts: FontFamily[]) {
        FontManager.fontFamilyDictionary = {};
        // 並び順はpriorityの早い順のグループごとに表示名で並べる。
        this.fontFamilyArray = fonts.slice().sort((a, b) => a.priority - b.priority
            || StringHelper.compareString(a.displayName, b.displayName))
        const L = fonts.length;
        // 連想配列にも保持
        FontManager.s_webfont_id_array = [];
        for (var i = 0; i < L; i++) {
            FontManager.fontFamilyDictionary[fonts[i].id] = fonts[i];
            if(!fonts[i].isLocalFont)
            {
                FontManager.s_webfont_id_array.push(fonts[i].id);
            }
        }
    }

    // フォントの検索
    public findFont(word : string) : FontFamily | null
    {
        var foundFont : FontFamily | null = null;
        var foundFonts : Array<FontFamily> = [];
        for(var i = 0; i < this.fontFamilyArray.length; ++i)
        {
            var font = this.fontFamilyArray[i];

            // webフォントは確認しない
            if(font.isLocalFont != true) continue;

            // 対象文字列からスペースを抜き、すべての英語を大文字にして比較する
            var wordTemp = word.replace(/ /g, "").toUpperCase();
            if(wordTemp == font.id.replace(/ /g, "").toUpperCase() ||
                wordTemp == font.displayName.replace(/ /g, "").toUpperCase() ||
                wordTemp == font.fullNameJP.replace(/ /g, "").toUpperCase() ||
                wordTemp == font.familyNameJP.replace(/ /g, "").toUpperCase())
            {
                foundFonts.push(font);
            }
        }

        if(foundFonts.length == 1)
        {
            // 見つかったフォントがただ一つの場合は決定
            foundFont = foundFonts[0];
        }
        else if(foundFonts.length > 1)
        {
            // 2つ以上のフォントが見つかった場合は、より一致度の高いものを選ぶ
            var maxMatch = 0;
            for(var i = 0; i < foundFonts.length; ++i)
            {
                var font = foundFonts[i];

                var wordTemp = word.replace(/ /g, "").toUpperCase();
                var match = 0;
                if(wordTemp == font.id.replace(/ /g, "").toUpperCase()) match++;
                if(wordTemp == font.displayName.replace(/ /g, "").toUpperCase()) match++;
                if(wordTemp == font.fullNameJP.replace(/ /g, "").toUpperCase()) match++;
                if(wordTemp == font.familyNameJP.replace(/ /g, "").toUpperCase()) match++;

                if(match > maxMatch)
                {
                    // 一致度が高い
                    maxMatch = match;
                    foundFont = font;
                }
            }
        }

        return foundFont;
    }
}
